import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, Spin, Alert } from 'antd';
import { ChevronDown, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { multichoixService } from '../../../services/multichoixService';
import { useAutoSelectSingleOption } from '../../../hooks/useAutoSelectSingleOption';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';

/**
 * Split comma-separated IDs into individual IDs
 */
const splitIds = (id) => {
  if (!id) return [];
  if (typeof id === 'string' && id.includes(',')) {
    return id.split(',').map((i) => i.trim()).filter(Boolean);
  }
  return [id];
};

const TreatmentTypeStepContent = ({
  step,
  value,
  selectionLabels,
  allSelections,
  onChange,
  onAutoAdvance,
}) => {
  const { t } = useTranslation('treatment-search');
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');

  // Transform selections for API
  const apiSelections = multichoixService.transformSelectionsForApi(allSelections);
  const hasCultures = apiSelections.cultures.length > 0;

  const { data, isLoading, error } = useQuery({
    queryKey: ['multichoix-step-3', apiSelections.cultures, apiSelections.cultural_practices],
    queryFn: () => multichoixService.getStepOptions(3, apiSelections),
    enabled: hasCultures,
    staleTime: 5 * 60 * 1000,
  });

  const treatmentTypes = data?.options || [];

  // SP-430: if only one treatment type is possible, select it and advance.
  const selectSingleOption = useCallback((opt) => {
    const ids = splitIds(opt.id);
    onChange(ids, [{ value: ids[0], label: opt.label }]);
  }, [onChange]);

  useAutoSelectSingleOption({
    options: treatmentTypes,
    isLoading,
    value,
    onSelect: selectSingleOption,
    onAdvance: onAutoAdvance,
  });

  // SP-430 M56/M57: manual selection (radio) commits the choice and advances.
  const handleManualSelect = useCallback((optionId, optionLabel) => {
    const ids = splitIds(optionId);
    onChange(ids, [{
      value: ids[0],
      label: optionLabel?.replace(/\s*\(\d+\)$/, '') || optionId,
    }]);
    if (onAutoAdvance) {
      setTimeout(onAutoAdvance, 100);
    }
  }, [onChange, onAutoAdvance]);

  // Find the current selected option based on URL values
  // URL may have individual IDs that were split from a comma-separated option
  const currentValue = useMemo(() => {
    if (!value || value.length === 0) return undefined;

    // Find the option whose IDs include any of the current values
    const matchingOption = treatmentTypes.find((type) => {
      const optionIds = splitIds(type.id);
      return value.some((v) => optionIds.includes(v));
    });

    return matchingOption?.id;
  }, [value, treatmentTypes]);

  // Mobile: Handle selection — commit, close the sheet and advance.
  const handleMobileSelect = useCallback((optionId, optionLabel) => {
    setSheetOpen(false);
    handleManualSelect(optionId, optionLabel);
  }, [handleManualSelect]);

  // Mobile: Get display value
  const mobileDisplayValue = useMemo(() => {
    if (!currentValue) return '';
    const option = treatmentTypes.find((t) => t.id === currentValue);
    return option?.label?.replace(/\s*\(\d+\)$/, '') || currentValue;
  }, [currentValue, treatmentTypes]);

  // Mobile: Filter options
  const filteredOptions = useMemo(() => {
    if (!mobileSearchValue) return treatmentTypes;
    const search = mobileSearchValue.toLowerCase();
    return treatmentTypes.filter((t) =>
      t.label.toLowerCase().includes(search)
    );
  }, [treatmentTypes, mobileSearchValue]);

  if (!hasCultures) {
    return (
      <Alert
        type="info"
        message={t('multichoix.steps.treatment_type.select_culture_first', 'Sélectionnez d\'abord une culture')}
        showIcon
        className="text-xs sm:text-sm"
      />
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={t('common.error', 'Une erreur est survenue')}
        description={error.message}
        showIcon
      />
    );
  }

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full">
        {/* Mobile trigger */}
        <div
          onClick={() => setSheetOpen(true)}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span className={`text-xs flex-1 truncate ${mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'}`}>
            {mobileDisplayValue || t(step.placeholder)}
          </span>
          {isLoading ? (
            <Spin size="small" className="ml-2" />
          ) : (
            <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
          )}
        </div>

        {/* Mobile sheet */}
        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t(step.title)}
        >
          {/* Search */}
          <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                placeholder={t('common|Rechercher...')}
                value={mobileSearchValue}
                onChange={(e) => setMobileSearchValue(e.target.value)}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spin size="default" />
              </div>
            )}

            {!isLoading && filteredOptions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                <div className="text-xs">{t('common|Aucun résultat trouvé')}</div>
              </div>
            )}

            {!isLoading && filteredOptions.length > 0 && (
              <div className="py-1">
                {filteredOptions.map((type) => {
                  const isSelected = type.id === currentValue;
                  return (
                    <div
                      key={type.id}
                      className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${isSelected ? 'bg-green-50' : ''}`}
                      onClick={() => handleMobileSelect(type.id, type.label)}
                    >
                      <div
                        className={`w-4 h-4 border-2 rounded-full mr-3 flex-shrink-0 flex items-center justify-center ${
                          isSelected ? 'border-[#207d54]' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <div className="w-2 h-2 bg-[#207d54] rounded-full" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">{type.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // Desktop render — SP-430 M56/E56: radio buttons stacked vertically (max 7),
  // single choice, auto-advance on selection.
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip={t('common.loading', 'Chargement...')} />
      </div>
    );
  }

  if (treatmentTypes.length === 0) {
    return (
      <Alert
        type="warning"
        message={t('multichoix.steps.treatment_type.empty', 'Aucun type de traitement disponible')}
        showIcon
        className="text-xs sm:text-sm"
      />
    );
  }

  return (
    <div className="w-full max-w-[600px] green-radio">
      <Radio.Group
        value={currentValue}
        onChange={(e) => {
          const opt = treatmentTypes.find((type) => type.id === e.target.value);
          if (opt) handleManualSelect(opt.id, opt.label);
        }}
        className="flex flex-col gap-1 max-h-[300px] overflow-y-auto"
      >
        {treatmentTypes.map((type) => (
          <Radio key={type.id} value={type.id} className="py-1 text-xs sm:text-sm">
            <span className="text-xs sm:text-sm">{type.label}</span>
          </Radio>
        ))}
      </Radio.Group>
    </div>
  );
};

export default TreatmentTypeStepContent;
